import { buildCategoryTotalsFromAssets, type CategoryValueInput } from "@/utils/portfolioMetrics";
import { distributeRoundedPercentages } from "@/utils/roundPercentages";

export interface AssetAllocationItem {
  category: string;
  amount: number;
  percentage: number;
}

export interface FinancialSnapshot {
  totalAssets: number;
  totalLiabilities: number;
  netWorth: number;
}

/** Sum of all asset current values — single source of truth for total assets. */
export function computeTotalAssetValue(
  assets: Array<{ amount?: number | null }> | null | undefined
): number {
  return (assets ?? []).reduce((sum, asset) => sum + (Number(asset.amount) || 0), 0);
}

/** Sum of outstanding liability balances — single source of truth for total liabilities. */
export function computeTotalLiabilityValue(
  liabilities:
    | Array<{ amount?: number | null; outstanding_balance?: number | null }>
    | null
    | undefined
): number {
  return (liabilities ?? []).reduce((sum, liability) => {
    const value =
      liability.amount != null
        ? Number(liability.amount)
        : Number(liability.outstanding_balance) || 0;
    return sum + (Number.isFinite(value) ? Math.max(0, value) : 0);
  }, 0);
}

/** Category breakdown with percentages that sum to 100% of total asset value. */
export function buildAssetAllocationBreakdown(assets: CategoryValueInput[]): AssetAllocationItem[] {
  const categoryTotals = buildCategoryTotalsFromAssets(assets);
  const entries = Object.entries(categoryTotals);
  const amounts = entries.map(([, amount]) => amount);
  const percentages = distributeRoundedPercentages(amounts);

  return entries
    .map(([category, amount], index) => ({
      category,
      amount,
      percentage: percentages[index] ?? 0,
    }))
    .sort((a, b) => b.amount - a.amount);
}

/** Net worth from assets minus liabilities (defaults liabilities to 0 when not tracked). */
export function computeFinancialSnapshot(
  totalAssets: number,
  totalLiabilities: number | null | undefined = 0
): FinancialSnapshot {
  const assets = Math.max(0, Number(totalAssets) || 0);
  const liabilities = Math.max(0, Number(totalLiabilities) || 0);

  return {
    totalAssets: assets,
    totalLiabilities: liabilities,
    netWorth: assets - liabilities,
  };
}

/**
 * Reconciliation helper: verifies module totals match within tolerance (INR paise rounding).
 */
export function assetsTotalsMatch(
  primaryTotal: number,
  secondaryTotal: number,
  toleranceInr = 1
): boolean {
  return Math.abs(primaryTotal - secondaryTotal) <= toleranceInr;
}

/** Returns true when every module total matches the first value within tolerance. */
export function reconcileModuleTotals(totals: number[], toleranceInr = 1): boolean {
  if (totals.length <= 1) {
    return true;
  }

  const [primary, ...rest] = totals;
  return rest.every((total) => assetsTotalsMatch(primary, total, toleranceInr));
}
